/**
 * PatternIQ Admin - Profile Management
 * =====================================
 * Phase B2: Tenant & User Profiles
 * 
 * UI for managing tenant and user preferences for agent personalization
 */

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  Settings, 
  User, 
  Building2, 
  Target, 
  TrendingUp, 
  Package,
  Save,
  RefreshCw,
  CheckCircle,
  AlertCircle
} from 'lucide-react';

// Available metrics for focus selection
const AVAILABLE_METRICS = [
  { id: 'revenue', name: 'Revenue', description: 'Total sales dollars' },
  { id: 'units', name: 'Units Sold', description: 'Total units volume' },
  { id: 'inventory_risk', name: 'Inventory Risk', description: 'Stockout and overstock risks' },
  { id: 'promo_roi', name: 'Promo ROI', description: 'Promotion effectiveness' },
  { id: 'margin', name: 'Margin', description: 'Gross margin percentage' },
  { id: 'sell_through', name: 'Sell-Through', description: 'Sell-through rate' },
  { id: 'wos', name: 'Weeks of Supply', description: 'Inventory coverage' },
];

// Available retailers (using actual UUIDs from database)
const AVAILABLE_RETAILERS = [
  { id: '10000000-0000-0000-0000-000000000001', name: 'Ulta Beauty' },
  { id: '10000000-0000-0000-0000-000000000003', name: "Macy's" },
  { id: '10000000-0000-0000-0000-000000000004', name: 'JCPenney' },
];

// Available agents
const AVAILABLE_AGENTS = [
  { id: 'ulta_merchandising', name: 'Ulta Merchandising' },
  { id: 'ulta_dotcom_performance', name: 'Ulta Dotcom' },
  { id: 'inventory_risk_replenishment', name: 'Inventory Risk' },
  { id: 'promo_pricing', name: 'Promo & Pricing' },
  { id: 'macys_performance', name: "Macy's Performance" },
  { id: 'forecast_explainer', name: 'Forecast Explainer' },
  { id: 'executive_summary', name: 'Executive Summary' },
  { id: 'data_quality_guardian', name: 'Data Quality' },
];

export default function Profiles() {
  const queryClient = useQueryClient();
  
  const [activeTab, setActiveTab] = useState<'tenant' | 'user'>('tenant');
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  
  // Tenant profile state
  const [focusMetrics, setFocusMetrics] = useState<string[]>([]);
  const [focusSkus, setFocusSkus] = useState<string>('');
  const [preferredHorizon, setPreferredHorizon] = useState(4);
  const [defaultRetailer, setDefaultRetailer] = useState<string>('');
  
  // User profile state
  const [defaultAgent, setDefaultAgent] = useState<string>('');
  const [preferredRetailers, setPreferredRetailers] = useState<string[]>([]);
  
  // Fetch tenant profile (API gets company from auth token)
  const { data: tenantProfile, isLoading: loadingTenant } = useQuery({
    queryKey: ['tenant-profile'],
    queryFn: async () => {
      const res = await fetch('/api/profiles/tenant');
      if (!res.ok) throw new Error('Failed to fetch tenant profile');
      const data = await res.json();
      
      // Update local state
      if (data.data) {
        setFocusMetrics(data.data.focusMetrics || []);
        setFocusSkus((data.data.focusSkus || []).join(', '));
        setPreferredHorizon(data.data.preferredHorizonWeeks || 4);
        setDefaultRetailer(data.data.defaultRetailerId || '');
      }
      
      return data.data;
    },
    enabled: activeTab === 'tenant'
  });
  
  // Fetch user profile (API gets user from auth token)
  const { data: userProfile, isLoading: loadingUser } = useQuery({
    queryKey: ['user-profile'],
    queryFn: async () => {
      const res = await fetch('/api/profiles/user');
      if (!res.ok) throw new Error('Failed to fetch user profile');
      const data = await res.json();
      
      // Update local state
      if (data.data) {
        setDefaultAgent(data.data.defaultAgentId || '');
        setPreferredRetailers(data.data.preferredRetailers || []);
      }
      
      return data.data;
    },
    enabled: activeTab === 'user'
  });
  
  // Save tenant profile (API gets company from auth token)
  const saveTenantMutation = useMutation({
    mutationFn: async () => {
      const res = await fetch('/api/profiles/tenant', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          focusMetrics,
          focusSkus: focusSkus.split(',').map(s => s.trim()).filter(Boolean),
          preferredHorizonWeeks: preferredHorizon,
          defaultRetailerId: defaultRetailer || null
        })
      });
      if (!res.ok) throw new Error('Failed to save tenant profile');
      return res.json();
    },
    onSuccess: () => {
      setSaveStatus('saved');
      queryClient.invalidateQueries({ queryKey: ['tenant-profile'] });
      setTimeout(() => setSaveStatus('idle'), 2000);
    },
    onError: () => {
      setSaveStatus('error');
      setTimeout(() => setSaveStatus('idle'), 3000);
    }
  });
  
  // Save user profile (API gets user from auth token)
  const saveUserMutation = useMutation({
    mutationFn: async () => {
      const res = await fetch('/api/profiles/user', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          defaultAgentId: defaultAgent || null,
          preferredRetailers
        })
      });
      if (!res.ok) throw new Error('Failed to save user profile');
      return res.json();
    },
    onSuccess: () => {
      setSaveStatus('saved');
      queryClient.invalidateQueries({ queryKey: ['user-profile'] });
      setTimeout(() => setSaveStatus('idle'), 2000);
    },
    onError: () => {
      setSaveStatus('error');
      setTimeout(() => setSaveStatus('idle'), 3000);
    }
  });
  
  const handleSave = () => {
    setSaveStatus('saving');
    if (activeTab === 'tenant') {
      saveTenantMutation.mutate();
    } else {
      saveUserMutation.mutate();
    }
  };
  
  const toggleMetric = (metricId: string) => {
    setFocusMetrics(prev => 
      prev.includes(metricId) 
        ? prev.filter(m => m !== metricId)
        : [...prev, metricId]
    );
  };
  
  const toggleRetailer = (retailerId: string) => {
    setPreferredRetailers(prev =>
      prev.includes(retailerId)
        ? prev.filter(r => r !== retailerId)
        : [...prev, retailerId]
    );
  };
  
  const isLoading = loadingTenant || loadingUser;
  const isSaving = saveTenantMutation.isPending || saveUserMutation.isPending;
  
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <Settings className="w-8 h-8 text-indigo-600" />
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
              Agent Personalization
            </h1>
          </div>
          <p className="text-gray-600 dark:text-gray-400">
            Configure how AI agents personalize their responses for your organization and preferences.
          </p>
        </div>
        
        {/* Tab Navigation */}
        <div className="flex gap-2 mb-6">
          <button
            onClick={() => setActiveTab('tenant')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
              activeTab === 'tenant'
                ? 'bg-indigo-600 text-white'
                : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
            }`}
          >
            <Building2 className="w-4 h-4" />
            Company Settings
          </button>
          <button
            onClick={() => setActiveTab('user')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
              activeTab === 'user'
                ? 'bg-indigo-600 text-white'
                : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
            }`}
          >
            <User className="w-4 h-4" />
            My Preferences
          </button>
        </div>
        
        {/* Content */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="w-6 h-6 text-gray-400 animate-spin" />
            </div>
          ) : activeTab === 'tenant' ? (
            <div className="p-6 space-y-6">
              {/* Focus Metrics */}
              <div>
                <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                  <Target className="w-4 h-4" />
                  Focus Metrics
                </label>
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">
                  Select the KPIs your company cares most about. Agents will prioritize these in their analysis.
                </p>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  {AVAILABLE_METRICS.map(metric => (
                    <button
                      key={metric.id}
                      onClick={() => toggleMetric(metric.id)}
                      className={`p-3 rounded-lg text-left transition-colors ${
                        focusMetrics.includes(metric.id)
                          ? 'bg-indigo-100 dark:bg-indigo-900/30 border-2 border-indigo-500'
                          : 'bg-gray-50 dark:bg-gray-700 border-2 border-transparent hover:border-gray-300'
                      }`}
                    >
                      <div className="font-medium text-sm text-gray-900 dark:text-white">
                        {metric.name}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        {metric.description}
                      </div>
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Focus SKUs */}
              <div>
                <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  <Package className="w-4 h-4" />
                  Focus SKUs
                </label>
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">
                  Enter high-priority SKU IDs (comma-separated). Agents will highlight these in their analysis.
                </p>
                <input
                  type="text"
                  value={focusSkus}
                  onChange={(e) => setFocusSkus(e.target.value)}
                  placeholder="e.g., SKU123, SKU456, SKU789"
                  className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
              </div>
              
              {/* Preferred Horizon */}
              <div>
                <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  <TrendingUp className="w-4 h-4" />
                  Forecast Horizon
                </label>
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">
                  Default number of weeks for forecasts and projections.
                </p>
                <select
                  value={preferredHorizon}
                  onChange={(e) => setPreferredHorizon(Number(e.target.value))}
                  className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500"
                >
                  <option value={2}>2 weeks</option>
                  <option value={4}>4 weeks</option>
                  <option value={8}>8 weeks</option>
                  <option value={12}>12 weeks</option>
                  <option value={26}>26 weeks (6 months)</option>
                </select>
              </div>
              
              {/* Default Retailer */}
              <div>
                <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  <Building2 className="w-4 h-4" />
                  Default Retailer
                </label>
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">
                  Pre-select a retailer for new conversations.
                </p>
                <select
                  value={defaultRetailer}
                  onChange={(e) => setDefaultRetailer(e.target.value)}
                  className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="">None (ask each time)</option>
                  {AVAILABLE_RETAILERS.map(r => (
                    <option key={r.id} value={r.id}>{r.name}</option>
                  ))}
                </select>
              </div>
            </div>
          ) : (
            <div className="p-6 space-y-6">
              {/* User Info */}
              <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 mb-4">
                <div className="text-sm text-gray-500 dark:text-gray-400">Logged in as</div>
                <div className="font-medium text-gray-900 dark:text-white">{userEmail}</div>
              </div>
              
              {/* Default Agent */}
              <div>
                <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  <User className="w-4 h-4" />
                  Default Agent
                </label>
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">
                  Pre-select an agent for new conversations.
                </p>
                <select
                  value={defaultAgent}
                  onChange={(e) => setDefaultAgent(e.target.value)}
                  className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="">None (auto-detect)</option>
                  {AVAILABLE_AGENTS.map(a => (
                    <option key={a.id} value={a.id}>{a.name}</option>
                  ))}
                </select>
              </div>
              
              {/* Preferred Retailers */}
              <div>
                <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  <Building2 className="w-4 h-4" />
                  Preferred Retailers
                </label>
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">
                  Select retailers you typically analyze. Agents will use this for context.
                </p>
                <div className="flex gap-2">
                  {AVAILABLE_RETAILERS.map(retailer => (
                    <button
                      key={retailer.id}
                      onClick={() => toggleRetailer(retailer.id)}
                      className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                        preferredRetailers.includes(retailer.id)
                          ? 'bg-indigo-600 text-white'
                          : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                      }`}
                    >
                      {retailer.name}
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Recent Topics (read-only) */}
              {userProfile?.recentTopics?.length > 0 && (
                <div>
                  <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                    Recent Topics
                  </label>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">
                    Topics you've recently discussed with agents (auto-tracked).
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {userProfile.recentTopics.map((topic: string, i: number) => (
                      <span
                        key={i}
                        className="px-3 py-1 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-full text-sm"
                      >
                        {topic}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          
          {/* Save Button */}
          <div className="px-6 py-4 bg-gray-50 dark:bg-gray-700/50 border-t border-gray-200 dark:border-gray-700 rounded-b-xl">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-500 dark:text-gray-400">
                {saveStatus === 'saved' && (
                  <span className="flex items-center gap-1 text-green-600">
                    <CheckCircle className="w-4 h-4" />
                    Changes saved
                  </span>
                )}
                {saveStatus === 'error' && (
                  <span className="flex items-center gap-1 text-red-600">
                    <AlertCircle className="w-4 h-4" />
                    Failed to save
                  </span>
                )}
              </div>
              <button
                onClick={handleSave}
                disabled={isSaving}
                className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg font-medium hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isSaving ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : (
                  <Save className="w-4 h-4" />
                )}
                Save Changes
              </button>
            </div>
          </div>
        </div>
        
        {/* Info Box */}
        <div className="mt-6 bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 border border-blue-200 dark:border-blue-800">
          <h3 className="font-semibold text-blue-900 dark:text-blue-100 mb-2">
            How Personalization Works
          </h3>
          <ul className="text-sm text-blue-800 dark:text-blue-200 space-y-1">
            <li>• <strong>Company Settings</strong> apply to all users in your organization</li>
            <li>• <strong>My Preferences</strong> are personal and only affect your experience</li>
            <li>• Agents use these settings to prioritize metrics and tailor responses</li>
            <li>• Changes take effect immediately for new conversations</li>
          </ul>
        </div>
      </div>
    </div>
  );
}

